import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, BrainCircuit, Code, Puzzle, ChevronDown, ChevronRight, TrendingUp } from 'lucide-react';
import {
  ConceptCategory, Difficulty, Exercise, TopicDepth, LearningMode, NodeStatus,
  ALL_NODES, PYJS_ALL_NODES, GODOT_ALL_NODES, BEGINNER_PATH, GODOT_BEGINNER_PATH, PYJS_BEGINNER_PATH
} from '@unity-learning/shared';
import { generateExercise, getDifficultySettings, getLearningPathProgress } from '../services/api';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import ComebackBanner from '../components/ComebackBanner';
import AdaptiveSuggestionCard from '../components/AdaptiveSuggestionCard';
import LearningPath from '../components/LearningPath';
import QuestSection from '../components/QuestSection';
import OnboardingQuestionnaire from '../components/OnboardingQuestionnaire';
import { trackEvent } from '../utils/analytics';
import { buildPathCustomPrompt } from '../utils/followUpActions';

// Rendu des identifiants de code (`GetComponent`…) dans les descriptions de catégories.
// Même convention de backticks et mêmes styles que QuestionPrompt (certif IA).
const renderInlineCode = (text: string): React.ReactNode[] =>
  text.split(/(`[^`]+`)/g).filter(Boolean).map((part, i) =>
    part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
      <code
        key={`c${i}`}
        className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/60 font-mono text-[0.9em] text-blue-700 dark:text-blue-300"
      >
        {part.slice(1, -1)}
      </code>
    ) : (
      <React.Fragment key={`t${i}`}>{part}</React.Fragment>
    )
  );

interface CategoryCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  concepts: ConceptCategory[];
  difficulty: Difficulty;
  onStart: (concepts: ConceptCategory[], difficulty: Difficulty, depth?: TopicDepth) => void;
  loading: boolean;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ title, description, icon, concepts, difficulty, onStart, loading }) => {
  const [showDepthSelection, setShowDepthSelection] = useState(false);

  const handleDepthSelection = (depth: TopicDepth) => {
    setShowDepthSelection(false);
    onStart(concepts, difficulty, depth);
  };

  return (
    <div className="glass dark:glass-dark rounded-xl p-6 flex flex-col card-hover">
      <div className="flex-grow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center mr-4">
                {icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {title}
              </h3>
            </div>
            <p className="text-gray-700 dark:text-slate-300 text-[0.95rem] leading-relaxed">
              {renderInlineCode(description)}
            </p>
          </div>
        </div>
      </div>

      {/* Depth Selection */}
      {showDepthSelection && (
        <div className="mb-4 space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Choisissez le niveau :</p>
          <div className="space-y-2">
            <button
              onClick={() => handleDepthSelection(TopicDepth.BASIC)}
              className="w-full text-left p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
            >
              <div className="font-medium text-blue-900 dark:text-blue-300">Niveau Débutant</div>
              <div className="text-sm text-blue-700 dark:text-blue-400">Concepts de base et fondamentaux</div>
            </button>
            <button
              onClick={() => handleDepthSelection(TopicDepth.ADVANCED)}
              className="w-full text-left p-3 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40 rounded-lg transition-colors"
            >
              <div className="font-medium text-purple-900 dark:text-purple-300">Niveau Avancé</div>
              <div className="text-sm text-purple-700 dark:text-purple-400">Concepts complexes et intégrations</div>
            </button>
          </div>
          <button
            onClick={() => setShowDepthSelection(false)}
            className="w-full text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mt-2"
          >
            Annuler
          </button>
        </div>
      )}

      <button
        onClick={() => {
          if (showDepthSelection) {
            setShowDepthSelection(false);
          } else {
            setShowDepthSelection(true);
          }
        }}
        disabled={loading}
        className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
      >
        <Play className="w-4 h-4 mr-1" />
        {loading ? 'Génération...' : showDepthSelection ? 'Fermer' : 'Choisir le niveau'}
        {!loading && !showDepthSelection && <ChevronDown className="w-4 h-4 ml-1" />}
      </button>
    </div>
  );
};

const pythonJsCategories = [
  {
    title: 'Variables et types',
    description: 'Déclarer et utiliser des variables (`int`, `str`, `float`, `bool`)',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Affichage',
    description: '`print()` / `console.log()` - Afficher du texte et des variables',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.PYJS_PRINT_OUTPUT, ConceptCategory.PYJS_STRINGS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Saisie utilisateur',
    description: '`input()` / `prompt()` - Demander une valeur à l\'utilisateur',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.PYJS_USER_INPUT, ConceptCategory.VARIABLES],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Opérateurs',
    description: 'Arithmétiques (`+`, `-`, `*`, `/`), comparaison (`==`, `!=`, `<`, `>`), logiques (`and`/`or`)',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.OPERATORS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Conditions',
    description: '`if`, `elif`/`else if`, `else` - Prendre des décisions dans le code',
    icon: <BrainCircuit className="w-6 h-6" />,
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Boucles',
    description: '`for`, `while` - Répéter des actions',
    icon: <BrainCircuit className="w-6 h-6" />,
    concepts: [ConceptCategory.LOOPS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Fonctions',
    description: '`def` / `function` - Créer et appeler des fonctions',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.METHODS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Chaînes de caractères',
    description: 'Manipulation de texte : concaténation, slicing, méthodes',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.PYJS_STRINGS, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Listes et tableaux',
    description: 'Créer, accéder et manipuler des collections',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.PYJS_LISTS_ARRAYS, ConceptCategory.LOOPS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Dictionnaires / Objets',
    description: 'Paires clé-valeur pour organiser les données',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.PYJS_DICTIONARIES],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Conversion de types',
    description: '`int()`, `str()`, `float()` / `parseInt()`, `String()` - Convertir entre types',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.PYJS_TYPE_CONVERSION, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Commentaires',
    description: '`#` ou `//` - Documenter et expliquer son code',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.COMMENTS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Identification d\'erreurs',
    description: 'Trouver et corriger les erreurs courantes',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.ERROR_IDENTIFICATION],
    difficulty: Difficulty.BEGINNER,
  },
];

const godotCategories = [
  {
    title: 'Variables et types',
    description: '`var`, `const`, `int`, `float`, `String`, `bool` - Stocker les données du jeu',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.DATA_TYPES],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Affichage',
    description: '`print()` - Afficher du texte et des variables dans la console Godot',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_PRINT_OUTPUT, ConceptCategory.VARIABLES],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Opérateurs',
    description: 'Arithmétiques (`+`, `-`, `*`, `/`), comparaison (`==`, `!=`, `<`, `>`), logiques (`and`/`or`)',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.OPERATORS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Conditions',
    description: '`if`, `elif`, `else` - Prendre des décisions dans le code',
    icon: <BrainCircuit className="w-6 h-6" />,
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Boucles',
    description: '`for`, `while`, `range()` - Répéter des actions',
    icon: <BrainCircuit className="w-6 h-6" />,
    concepts: [ConceptCategory.LOOPS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Fonctions',
    description: '`func`, `return`, paramètres - Créer et appeler des fonctions',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.METHODS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Array & Dictionary',
    description: 'Inventaires, scores, ressources : créer et manipuler des collections',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_ARRAYS_DICTS, ConceptCategory.LOOPS],
    difficulty: Difficulty.BEGINNER,
  },
  {
    title: 'Nodes et Scènes',
    description: '`extends`, `$`, `get_node()`, `@onready`, `@export` - Scripter les nodes',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_NODES_SCENES],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Vecteurs (Vector2)',
    description: '`Vector2`, `normalized()`, `distance_to()`, `lerp` - Le langage du mouvement 2D',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_VECTOR_MATH],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Instancier des scènes',
    description: '`preload`, `instantiate()`, `add_child()` - Créer ennemis et projectiles par code',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_SCENE_INSTANCING],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Mouvement du personnage',
    description: '`CharacterBody2D`, `velocity`, `move_and_slide()`, gravité et saut',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_CHARACTER_MOVEMENT],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Cycle de vie',
    description: '`_ready()`, `_process(delta)`, `_physics_process()` - Le rythme du jeu',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_LIFECYCLE],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Signaux',
    description: '`signal`, `emit()`, `connect()` - Faire communiquer les nodes',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_SIGNALS],
    difficulty: Difficulty.INTERMEDIATE,
  },
  {
    title: 'Await et Callbacks',
    description: '`await`, `Callable`, lambdas, timers - L\'asynchrone dans Godot',
    icon: <Puzzle className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_AWAIT_CALLABLES],
    difficulty: Difficulty.ADVANCED,
  },
  {
    title: 'GDScript avancé',
    description: '`match`, `enum`, `class_name`, typage statique - Les mots-clés avancés',
    icon: <BrainCircuit className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_TYPED_GDSCRIPT],
    difficulty: Difficulty.ADVANCED,
  },
  {
    title: 'POO et patterns de jeu',
    description: 'Héritage, `super`, `set`/`get`, Resources custom, state machines, `Tween`',
    icon: <BrainCircuit className="w-6 h-6" />,
    concepts: [ConceptCategory.GODOT_OOP_PATTERNS],
    difficulty: Difficulty.ADVANCED,
  },
  {
    title: 'Identification d\'erreurs',
    description: 'Trouver et corriger les erreurs courantes en GDScript',
    icon: <Code className="w-6 h-6" />,
    concepts: [ConceptCategory.ERROR_IDENTIFICATION],
    difficulty: Difficulty.BEGINNER,
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useUser();
  const { learningMode, programmingLanguage, isPythonJsMode, isGodotMode, syncWithUser } = useLearningMode();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showCustomDepths, setShowCustomDepths] = useState(false);
  const [customDepthOptions, setCustomDepthOptions] = useState<TopicDepth[]>([]);
  // Premiers pas : tant qu'aucune étape du parcours du mode courant n'est terminée, l'accueil
  // ne montre qu'un point d'entrée (le parcours, étape 1 ouverte) ; le reste est replié sous
  // « Plus d'options ». null = progression pas encore chargée (rien d'autre n'est affiché).
  // Les admins voient toujours l'accueil complet.
  const [firstSteps, setFirstSteps] = useState<boolean | null>(null);
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);

  const unityCsharpCategories = [
    // === FONDAMENTAUX C# POUR UNITY ===
    {
      title: 'Types de données fondamentaux',
      description: '`int`, `string`, `bool`, `float`, `Vector3`, tableaux et `List<T>`',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.DATA_TYPES, ConceptCategory.VARIABLES],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Déclaration et initialisation',
      description: 'Variables, assignation et gestion mémoire',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.VARIABLES, ConceptCategory.DATA_TYPES],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Modificateurs d\'accès',
      description: '`public`, `private`, `[SerializeField]` et visibilité Inspector',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.ACCESS_MODIFIERS, ConceptCategory.VARIABLES],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Signatures de méthodes',
      description: 'Types de retour (`void`, `string`, `IEnumerator`), paramètres',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.METHODS, ConceptCategory.DATA_TYPES],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Opérateurs C#',
      description: 'Arithmétiques, comparaison, logiques, incrémentation',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.OPERATORS],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Instructions conditionnelles',
      description: '`if`, `else if`, `else` - logique de branchement',
      icon: <BrainCircuit className="w-6 h-6" />,
      concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.OPERATORS],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Boucles et itération',
      description: '`for`, `foreach` - répétition et parcours de collections',
      icon: <BrainCircuit className="w-6 h-6" />,
      concepts: [ConceptCategory.LOOPS, ConceptCategory.DATA_TYPES],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Énumérations (enum)',
      description: 'Création d\'`enum` et utilisation dans `switch`',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.ENUMS, ConceptCategory.CONDITIONALS],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Commentaires et documentation',
      description: 'Bonnes pratiques de documentation avec `//`',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.COMMENTS],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Débogage et erreurs',
      description: 'Identifier erreurs de type, syntaxe et groupe de méthodes',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.ERROR_IDENTIFICATION, ConceptCategory.DEBUGGING],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Conventions de nommage',
      description: 'PascalCase pour méthodes et types, bonnes pratiques',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.NAMING_CONVENTIONS],
      difficulty: Difficulty.BEGINNER,
    },

    // === API UNITY & CONCEPTS DE SCRIPTING ===
    {
      title: 'Cycle de vie MonoBehaviour',
      description: '`Awake()`, `Start()`, `Update()`, `FixedUpdate()`, `LateUpdate()`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.MONOBEHAVIOUR_LIFECYCLE],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Gestion des entrées clavier',
      description: '`Input.GetKey()`, `Input.GetKeyDown()`, `Input.GetButtonDown()`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.INPUT_HANDLING],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Gestion de la souris',
      description: '`Input.mousePosition`, `Input.GetMouseButton()`, index des boutons',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.INPUT_HANDLING],
      difficulty: Difficulty.BEGINNER,
    },
    {
      title: 'Manipulation GameObject',
      description: '`GameObject.tag`, `FindGameObjectWithTag()`, `Destroy()`, `Instantiate()`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.GAMEOBJECT_MANIPULATION],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Accès aux composants',
      description: '`GetComponent<T>()` et interaction avec les composants',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.COMPONENT_ACCESS],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Physique avec Rigidbody2D',
      description: '`Rigidbody2D.linearVelocity`, forces et mouvement',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.PHYSICS_RIGIDBODY],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Détection de collision',
      description: '`OnCollisionEnter2D()`, `OnTriggerEnter2D()`, système de collision',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.PHYSICS_RIGIDBODY, ConceptCategory.MONOBEHAVIOUR_LIFECYCLE],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Mathématiques vectorielles',
      description: '`Vector3`, `Vector2`, `Random.Range()`, `transform.TransformDirection()`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.VECTOR_MATH],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Mouvement et rotation',
      description: '`Vector3.MoveTowards()`, `Quaternion.identity`, `Time.deltaTime`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.VECTOR_MATH],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Coroutines de base',
      description: '`IEnumerator`, `StartCoroutine()`, `yield return new WaitForSeconds()`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.COROUTINES],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Coroutines avec paramètres',
      description: 'Passage de paramètres aux coroutines, gestion complexe',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.COROUTINES, ConceptCategory.METHODS],
      difficulty: Difficulty.ADVANCED,
    },
    {
      title: 'Classes abstraites',
      description: '`abstract class`, méthodes `abstract` et `override`',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.ABSTRACT_CLASSES],
      difficulty: Difficulty.ADVANCED,
    },
    {
      title: 'Interfaces',
      description: '`interface` et son implémentation par une classe',
      icon: <Code className="w-6 h-6" />,
      concepts: [ConceptCategory.INTERFACES],
      difficulty: Difficulty.ADVANCED,
    },
    {
      title: 'Système d\'animation',
      description: '`anim.SetTrigger()`, `anim.SetInteger()`, `anim.SetBool()`, `anim.Play()`',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.ANIMATION_SYSTEM],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Interface utilisateur (UI)',
      description: '`TextMeshProUGUI.text`, répondre aux clics de bouton via script',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.UI_INTERACTION],
      difficulty: Difficulty.INTERMEDIATE,
    },
    {
      title: 'Débogage Unity',
      description: '`Debug.Log()`, outils de débogage et bonnes pratiques',
      icon: <Puzzle className="w-6 h-6" />,
      concepts: [ConceptCategory.DEBUGGING],
      difficulty: Difficulty.BEGINNER,
    }
  ];

  const categories = isPythonJsMode ? pythonJsCategories : isGodotMode ? godotCategories : unityCsharpCategories;

  // Sections de l'exploration libre, avec titres et regroupements propres à chaque mode
  const freeExplorationSections = isPythonJsMode
    ? [
      {
        title: 'Fondamentaux Python / JavaScript',
        icon: <Code className="w-5 h-5 mr-2 text-blue-600" />,
        cards: categories.filter(cat => cat.difficulty === Difficulty.BEGINNER),
      },
      {
        title: 'Niveau Intermédiaire',
        icon: <BrainCircuit className="w-5 h-5 mr-2 text-orange-600" />,
        cards: categories.filter(cat => cat.difficulty === Difficulty.INTERMEDIATE),
      },
      {
        title: 'Concepts Avancés',
        icon: <BrainCircuit className="w-5 h-5 mr-2 text-red-600" />,
        cards: categories.filter(cat => cat.difficulty === Difficulty.ADVANCED),
      },
    ]
    : isGodotMode
      ? [
        {
          title: 'Fondamentaux GDScript',
          icon: <Code className="w-5 h-5 mr-2 text-blue-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.BEGINNER),
        },
        {
          title: 'Scripting Godot - Niveau Intermédiaire',
          icon: <Puzzle className="w-5 h-5 mr-2 text-green-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.INTERMEDIATE),
        },
        {
          title: 'Concepts Avancés',
          icon: <BrainCircuit className="w-5 h-5 mr-2 text-red-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.ADVANCED),
        },
      ]
      : [
        {
          title: 'Fondamentaux C# pour Unity',
          icon: <Code className="w-5 h-5 mr-2 text-blue-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.BEGINNER && cat.concepts.every(c =>
            [ConceptCategory.DATA_TYPES, ConceptCategory.VARIABLES, ConceptCategory.ACCESS_MODIFIERS,
            ConceptCategory.METHODS, ConceptCategory.OPERATORS, ConceptCategory.CONDITIONALS,
            ConceptCategory.LOOPS, ConceptCategory.ENUMS, ConceptCategory.COMMENTS,
            ConceptCategory.ERROR_IDENTIFICATION, ConceptCategory.NAMING_CONVENTIONS].includes(c)
          )),
        },
        {
          title: 'API Unity - Niveau Débutant',
          icon: <Puzzle className="w-5 h-5 mr-2 text-green-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.BEGINNER && cat.concepts.some(c =>
            [ConceptCategory.MONOBEHAVIOUR_LIFECYCLE, ConceptCategory.INPUT_HANDLING, ConceptCategory.DEBUGGING].includes(c)
          )),
        },
        {
          title: 'API Unity - Niveau Intermédiaire',
          icon: <BrainCircuit className="w-5 h-5 mr-2 text-orange-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.INTERMEDIATE),
        },
        {
          title: 'Concepts Avancés',
          icon: <BrainCircuit className="w-5 h-5 mr-2 text-red-600" />,
          cards: categories.filter(cat => cat.difficulty === Difficulty.ADVANCED),
        },
      ];

  // Sync learning mode with backend on login
  useEffect(() => {
    if (user) syncWithUser(user.id);
  }, [user, syncWithUser]);

  // Premiers pas ou non : une étape du parcours du mode courant est-elle terminée ?
  // (le parcours lui-même charge la même progression ; un deuxième GET léger évite de
  // faire remonter un état depuis LearningPath). En cas d'échec, accueil complet.
  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin') {
      setFirstSteps(false);
      return;
    }
    let cancelled = false;
    setFirstSteps(null);
    setMoreOptionsOpen(false);
    const path = isPythonJsMode ? PYJS_BEGINNER_PATH : isGodotMode ? GODOT_BEGINNER_PATH : BEGINNER_PATH;
    const nodeIds = new Set(path.nodes.map(n => n.id));
    getLearningPathProgress(user.id)
      .then(progress => {
        if (cancelled) return;
        const hasCompletedNode = progress.some(p =>
          nodeIds.has(p.nodeId) && (p.status === NodeStatus.COMPLETED || p.status === NodeStatus.MASTERED)
        );
        setFirstSteps(!hasCompletedNode);
      })
      .catch(() => { if (!cancelled) setFirstSteps(false); });
    return () => { cancelled = true; };
  }, [user, isPythonJsMode, isGodotMode]);

  const openMoreOptions = () => {
    if (!moreOptionsOpen) trackEvent('home_more_options_opened', { learningMode });
    setMoreOptionsOpen(open => !open);
  };

  const handleGenerateCustomExercise = async (depth?: TopicDepth) => {
    if (!customPrompt.trim() || !user) return;

    trackEvent('custom_exercise_requested', { learningMode, ...(depth ? { depth } : {}) });

    try {
      setIsGenerating(true);
      setError(null);
      setShowCustomDepths(false);

      const customRequirements = depth ? `\nNiveau de profondeur : ${depth === TopicDepth.BASIC ? 'débutant' : 'avancé'}` : '';

      const exercise = await generateExercise({
        userId: user.id,
        customPrompt: customPrompt + customRequirements,
        adaptToProgress: false,
        learningMode,
        programmingLanguage
      });
      navigate(`/exercise/${exercise.id}`);
    } catch (err) {
      console.error('❌ Erreur lors de la génération d\'exercice personnalisé:', err);
      setError((err as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCustomPromptChange = (value: string) => {
    setCustomPrompt(value);
    // Auto-generate depth suggestions based on prompt content
    if (value.trim().length > 10) {
      setCustomDepthOptions([TopicDepth.BASIC, TopicDepth.ADVANCED]);
    } else {
      setCustomDepthOptions([]);
    }
  };

  const handleGenerateExercise = async (concepts: ConceptCategory[], difficulty: Difficulty, depth?: TopicDepth) => {
    if (!user) return;

    try {
      setIsGenerating(true);
      setError(null);

      let customRequirements = '';
      if (depth) {
        customRequirements = depth === TopicDepth.BASIC
          ? '\nNiveau: Concentre-toi sur les concepts fondamentaux et les bases'
          : '\nNiveau: Utilise des concepts avancés et des intégrations complexes';
      }

      const exercise = await generateExercise({
        userId: user.id,
        concepts,
        difficulty,
        customPrompt: customRequirements,
        adaptToProgress: false,
        learningMode,
        programmingLanguage
      });
      navigate(`/exercise/${exercise.id}`);
    } catch (err) {
      console.error('❌ Erreur lors de la génération d\'exercice:', err);
      setError((err as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  // B1 — Partie génération commune à handleLearningPathExercise et handleMiniCoursFirst.
  // On a sorti la logique de fetch pour ne pas la dupliquer (le deux chemins partagent le
  // même POST /api/exercises/generate avec concepts/difficulty/nodeId/learningMode/
  // programmingLanguage — piège n°1 documenté en B3 : learningMode DOIT être passé).
  // Retourne l'id du nouvel exercice, ou null en cas d'erreur (setError géré par l'appelant).
  const generateNodeExercise = async (
    concepts: ConceptCategory[],
    difficulty: Difficulty,
    nodeId: string,
    knowledgePointIds?: string[]
  ): Promise<Exercise | null> => {
    if (!user) return null;

    // Get user's difficulty settings (best-effort : sans eux, le backend prend ses défauts)
    const difficultySettings = await getDifficultySettings(user.id).catch(() => null);

    return generateExercise({
      userId: user.id,
      concepts,
      difficulty,
      adaptToProgress: true,
      learningMode,
      programmingLanguage,
      learningNodeId: nodeId, // Track which learning node this exercise is for
      ...(knowledgePointIds && { knowledgePointIds }), // Include knowledge points if specified
      ...(difficultySettings ? {
        algorithmicComplexity: difficultySettings.algorithmicComplexity,
        ...(learningMode === LearningMode.UNITY_CSHARP ? { unityApiKnowledge: difficultySettings.unityApiKnowledge } : {}),
        codeScope: difficultySettings.codeScope,
        cognitiveLoad: difficultySettings.cognitiveLoad
      } : {}),
      customPrompt: buildPathCustomPrompt(nodeId, knowledgePointIds)
    });
  };

  const handleLearningPathExercise = async (concepts: ConceptCategory[], difficulty: Difficulty, nodeId: string, knowledgePointIds?: string[]) => {
    try {
      setIsGenerating(true);
      setError(null);

      const exercise = await generateNodeExercise(concepts, difficulty, nodeId, knowledgePointIds);
      if (!exercise) return;

      // Navigate to exercise with learning node ID and knowledge points for progress tracking.
      // Sur une étape entière, c'est le serveur qui choisit le point visé et le renvoie.
      const urlParams = new URLSearchParams({ learningNodeId: nodeId });
      const targetedKps = exercise.knowledgePointIds ?? knowledgePointIds;
      if (targetedKps && targetedKps.length > 0) {
        urlParams.set('knowledgePointIds', targetedKps.join(','));
      }
      navigate(`/exercise/${exercise.id}?${urlParams.toString()}`);
    } catch (err) {
      console.error('❌ Erreur génération exercice parcours d\'apprentissage:', err);
      setError((err as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  // B1 — « Mini-cours d'abord » : même génération d'exercice que handleLearningPathExercise
  // (réutilise generateNodeExercise — pas de duplication du fetch), mais navigue vers la
  // NOUVELLE page /mini-cours/:id au lieu de /exercise/:id. L'étudiant voit d'abord le cours,
  // puis clique sur « Passer à l'exercice → » pour s'y mettre.
  const handleMiniCoursFirst = async (nodeId: string) => {
    try {
      setIsGenerating(true);
      setError(null);

      trackEvent('mini_cours_first_started', { nodeId, learningMode });

      // Le nœud vient des constantes shared (même curriculum que la DB) — on ne prend PAS
      // de knowledgePointIds ici : le mini-cours porte sur le concept entier du nœud.
      const exercise = await generateNodeExercise(
        // concepts/difficulty résolus depuis les constantes du curriculum du mode courant,
        // comme handleAdaptiveExercise/handleComebackExercise.
        (isPythonJsMode ? PYJS_ALL_NODES : isGodotMode ? GODOT_ALL_NODES : ALL_NODES).find(n => n.id === nodeId)?.concepts || [],
        (isPythonJsMode ? PYJS_ALL_NODES : isGodotMode ? GODOT_ALL_NODES : ALL_NODES).find(n => n.id === nodeId)?.difficulty || Difficulty.BEGINNER,
        nodeId
      );
      if (!exercise) return;

      // C2 — porter le nœud du parcours dans l'URL pour que sa progression s'incrémente
      // après complétion (MiniCoursFirstPage le réinjecte dans /exercise/:id?learningNodeId=,
      // qu'ExercisePage renvoie au submit → backend updateLearningPathProgress). Sans ça, le
      // flux « Mini-cours d'abord » ne fait jamais avancer le parcours, contrairement au
      // bouton « Commencer » direct (handleLearningPathExercise, ligne ~765).
      const miniCoursParams = new URLSearchParams({ learningNodeId: nodeId });
      if (exercise.knowledgePointIds?.length) {
        miniCoursParams.set('knowledgePointIds', exercise.knowledgePointIds.join(','));
      }
      navigate(`/mini-cours/${exercise.id}?${miniCoursParams.toString()}`);
    } catch (err) {
      console.error('❌ Erreur génération exercice (mini-cours d\'abord):', err);
      setError((err as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  // B3 — Relance d'un exercice sur un concept délaissé depuis le bandeau de retour.
  // Les concepts/difficulté du nœud viennent des constantes shared (même curriculum que la DB),
  // et handleLearningPathExercise passe learningMode + programmingLanguage (piège n°1).
  const handleComebackExercise = (nodeId: string, kpId: string) => {
    const nodes = isPythonJsMode ? PYJS_ALL_NODES : isGodotMode ? GODOT_ALL_NODES : ALL_NODES;
    const node = nodes.find(n => n.id === nodeId);
    if (!node) {
      console.warn('⚠️ Nœud inconnu pour le concept délaissé:', nodeId);
      return;
    }
    trackEvent('comeback_exercise_started', { learningMode, nodeId, kpId });
    handleLearningPathExercise(node.concepts, node.difficulty, nodeId, [kpId]);
  };

  // B10 — Relance d'un exercice depuis la suggestion adaptative : mêmes constantes de
  // curriculum que le bandeau de retour, mais SANS ciblage de point de connaissance
  // (la suggestion porte sur un concept entier) et sans événement comeback.
  const handleAdaptiveExercise = (nodeId: string) => {
    const nodes = isPythonJsMode ? PYJS_ALL_NODES : isGodotMode ? GODOT_ALL_NODES : ALL_NODES;
    const node = nodes.find(n => n.id === nodeId);
    if (!node) {
      console.warn('⚠️ Nœud inconnu pour la suggestion adaptative:', nodeId);
      return;
    }
    handleLearningPathExercise(node.concepts, node.difficulty, nodeId);
  };

  // Exploration libre, exercice sur mesure et lien Progression : affichés d'emblée sur l'accueil
  // complet, repliés sous « Plus d'options » pendant les premiers pas.
  const extraSections = (
    <>
        {/* Separator between guided and free-form learning */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-medium">
              ou explorez par catégories libres
            </span>
          </div>
        </div>

        {/* Traditional Categories Section */}
        <div className="rounded-xl p-6 border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Exploration libre par catégories</h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Vous préférez choisir vos propres sujets ? Explorez ces catégories organisées par niveau de difficulté.
              <span className="text-amber-600 dark:text-amber-400 font-medium"> Recommandé pour les étudiants expérimentés</span> qui souhaitent cibler des compétences spécifiques.
            </p>
          </div>

          {freeExplorationSections.filter(section => section.cards.length > 0).map(section => (
            <div key={section.title} className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                {section.icon}
                {section.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.cards.map((cat, index) => (
                  <CategoryCard
                    key={`${section.title}-${index}`}
                    title={cat.title}
                    description={cat.description}
                    icon={cat.icon}
                    concepts={cat.concepts}
                    difficulty={cat.difficulty}
                    onStart={handleGenerateExercise}
                    loading={isGenerating}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Custom Exercise Request */}
        <div className="glass dark:glass-dark rounded-xl p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 pointer-events-none"></div>
          <div className="relative z-10">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <BrainCircuit className="w-5 h-5 mr-2 text-blue-600" />
              Exercice sur mesure
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Décrivez le type d'exercice {isPythonJsMode ? 'Python/JavaScript' : isGodotMode ? 'Godot/GDScript' : 'Unity/C#'} que vous souhaitez pratiquer et l'IA générera un défi adapté.
            </p>
            <div className="space-y-4">
              <div className="flex-1">
                <textarea
                  value={customPrompt}
                  onChange={(e) => handleCustomPromptChange(e.target.value)}
                  placeholder={isPythonJsMode
                    ? "Ex: Je veux m'entraîner sur les boucles avec une liste de courses, créer un petit quiz, ou manipuler du texte..."
                    : isGodotMode
                      ? "Ex: Je veux créer un système de vie et de dégâts, faire communiquer deux nodes avec un signal, ou utiliser await avec un timer..."
                      : "Ex: Je veux apprendre à faire bouger un objet avec les touches WASD, ou créer un système de collecte d'objets, ou gérer des collisions..."}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  rows={3}
                  disabled={isGenerating}
                />
              </div>

              {/* Custom Exercise Depth Selection */}
              {showCustomDepths && customDepthOptions.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Choisissez le niveau :</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => handleGenerateCustomExercise(TopicDepth.BASIC)}
                      disabled={isGenerating}
                      className="p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors text-left disabled:opacity-50"
                    >
                      <div className="font-medium text-blue-900 dark:text-blue-300">Niveau Débutant</div>
                      <div className="text-sm text-blue-700 dark:text-blue-400">Concepts de base et fondamentaux</div>
                    </button>
                    <button
                      onClick={() => handleGenerateCustomExercise(TopicDepth.ADVANCED)}
                      disabled={isGenerating}
                      className="p-3 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40 rounded-lg transition-colors text-left disabled:opacity-50"
                    >
                      <div className="font-medium text-purple-900 dark:text-purple-300">Niveau Avancé</div>
                      <div className="text-sm text-purple-700 dark:text-purple-400">Concepts avancés et intégrations</div>
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => handleGenerateCustomExercise()}
                      disabled={isGenerating}
                      className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-200 text-sm transition-colors disabled:opacity-50"
                    >
                      Générer sans niveau spécifique
                    </button>
                    <button
                      onClick={() => setShowCustomDepths(false)}
                      className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm"
                      disabled={isGenerating}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    if (customDepthOptions.length > 0) {
                      if (showCustomDepths) {
                        setShowCustomDepths(false);
                      } else {
                        setShowCustomDepths(true);
                      }
                    } else {
                      handleGenerateCustomExercise();
                    }
                  }}
                  disabled={isGenerating || !customPrompt.trim()}
                  className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Génération...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Play className="w-4 h-4 mr-2" />
                      {customDepthOptions.length > 0 ? (showCustomDepths ? 'Fermer' : 'Choisir le niveau') : 'Générer'}
                      {!showCustomDepths && customDepthOptions.length > 0 && <ChevronDown className="w-4 h-4 ml-1" />}
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lien vers la page Progression (remplace l'ancien bloc de stats qui la dupliquait) */}
        <Link
          to="/progression"
          className="glass dark:glass-dark rounded-xl p-6 flex items-center justify-between card-hover group"
        >
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Votre progression</h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Exercices terminés, taux de réussite, maîtrise par concept : tout est sur la page Progression.
              </p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </Link>
    </>
  );

  if (userLoading) {
    return <div className="text-center p-8">Chargement de l'utilisateur...</div>;
  }

  const fullHome = firstSteps === false;
  const showOnboarding = user?.role === 'student' && !user.onboardingCompletedAt;

  return (
    <div className="space-y-8">
      {/* Questionnaire de niveau du premier login (une fois ; « Passer » le ferme aussi) */}
      {showOnboarding && <OnboardingQuestionnaire />}
      {/* C6 — Intro haut de page : bandeaux (retour B3 + suggestion B10) groupés avec le hero
          à un espacement resserré (16px). Le hero étant TOUJOURS rendu, ce groupe n'est jamais
          vide même quand les deux bandeaux renvoient null (pas de trou fantôme). Les grandes
          sections en dessous gardent leur respiration (space-y-8 = 32px). */}
      <div className="space-y-4">
      {/* B3 — Bilan de retour après absence : un seul bandeau compact et dismissible */}
      <ComebackBanner onStartKpExercise={handleComebackExercise} isGenerating={isGenerating} />
      {/* B10 — Suggestion adaptative passive (rule-based, sans IA) sous le bandeau de retour.
          Masquée pendant les premiers pas : « À découvrir » y répète l'étape 1 sous un autre nom. */}
      {fullHome && (
        <AdaptiveSuggestionCard
          onStartNodeExercise={handleAdaptiveExercise}
          isGenerating={isGenerating}
        />
      )}
      <div className="glass dark:glass-dark rounded-xl p-8 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
        <div className="relative z-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Bienvenue, {user?.pseudonym || 'Invité'} !
          </h1>
          {fullHome ? (
          <p className="text-gray-600 dark:text-slate-300 text-lg">
            Le <strong className="font-semibold text-gray-800 dark:text-slate-100">Parcours d'Apprentissage</strong> ci-dessous
            vous guide étape par étape : c'est le point de départ recommandé. Vous pouvez aussi explorer librement les
            catégories plus bas.
          </p>
          ) : (
          <p className="text-gray-600 dark:text-slate-300 text-lg">
            Tout commence par l'<strong className="font-semibold text-gray-800 dark:text-slate-100">étape 1 du parcours</strong> ci-dessous :
            cliquez sur « Commencer ». Les autres façons de s'exercer se débloquent une fois la première étape terminée.
          </p>
          )}

          {/* Débutant vs avancé : chaque carte de catégorie propose les deux niveaux (accueil complet seulement) */}
          {fullHome && (
          <div className="mt-5">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Débutant ou avancé ? Chaque catégorie propose les deux niveaux au moment de générer un exercice :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-blue-50/80 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="font-medium text-blue-900 dark:text-blue-300 text-sm">Niveau Débutant</div>
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  Concepts de base et fondamentaux — choisissez-le si vous découvrez la notion (et dans le doute).
                </p>
              </div>
              <div className="bg-purple-50/80 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                <div className="font-medium text-purple-900 dark:text-purple-300 text-sm">Niveau Avancé</div>
                <p className="text-sm text-purple-700 dark:text-purple-400">
                  Concepts complexes et intégrations — quand le niveau débutant ne vous résiste plus.
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      </div>
      {/* ^ fin du groupe intro C6 (bandeaux + hero) */}

      {/* Daily Quest Section */}
      <QuestSection />

      {/* Learning Path - le point d'entrée recommandé pour un nouvel utilisateur */}
      <div className="relative">
        <div className="absolute -top-3 left-6 z-10 inline-flex items-center px-3 py-1 rounded-full bg-orange-600 text-white text-xs font-semibold shadow-md">
          <Play className="w-3 h-3 mr-1.5" />
          Commencez ici
        </div>
        <LearningPath
          onStartExercise={handleLearningPathExercise}
          onStartMiniCours={handleMiniCoursFirst}
          isGenerating={isGenerating}
        />
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg" role="alert">
          <strong className="font-bold">Erreur : </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {fullHome ? extraSections : firstSteps === true ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40 p-4">
          <button
            type="button"
            onClick={openMoreOptions}
            aria-expanded={moreOptionsOpen}
            className="w-full flex items-center justify-between gap-4 text-left"
          >
            <span>
              <span className="block font-medium text-gray-800 dark:text-slate-100">Plus d'options</span>
              <span className="block text-sm text-gray-500 dark:text-gray-400">
                Exploration libre par catégories, exercice sur mesure, page Progression — accessibles dès maintenant,
                recommandées après la première étape.
              </span>
            </span>
            <ChevronDown className={`w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400 transition-transform ${moreOptionsOpen ? 'rotate-180' : ''}`} />
          </button>
          {moreOptionsOpen && <div className="mt-6 space-y-8">{extraSections}</div>}
        </div>
      ) : null}
    </div>
  );
};

export default HomePage;